/**
 * @file Parent-board modal form opener.
 */

import { appData, state } from "../../app/state/state.js";
import { getNextCaseFileNumber } from "../../domains/patients/patientApi.js";
import { findItem } from "../utils/itemLookup.js";
import {
    formatCurrency,
    isColorDark,
} from "../utils/displayUtils.js";
import {
    getAssignableUsersWithPermission,
} from "../utils/userDisplay.js";
import {
    applyBrandingVariables,
} from "../ui/brandingTokens.js";
import {
    createDateControl,
} from "../ui/controls/dateControl.js";
import {
    createProviderSelectPreset,
    createPrioritySelectPreset,
    createStatusSelectPreset,
    createStreamScopedStatusSelectPreset,
    createStreamScopedTypeSelectPreset,
    createTypeSelectPreset,
    createUserSelectPreset,
} from "../ui/controls/controlPresets.js";
import {
    createSelectControl,
} from "../ui/controls/selectControl.js";
import {
    destroyRegisteredControlsIn,
    focusFirstRegisteredControlError,
    validateRegisteredControls,
} from "../ui/controls/controlCore.js";
import {
    getScopedTypeOptions,
} from "../utils/typeScope.js";
import { handleParentFormSubmit, isParentFormType } from "./parentFormSubmit.js";
import {
    getParentFormAdapter,
} from "./parentFormRegistry.js";
import {
    getParentFormFields,
    setupTaskRecurrenceControls,
} from "./parentFieldRenderers.js";
import {
    applyAddFormContext,
    clearSpecialisedFormShellArtifacts,
    cloneFormItemData,
    getFormHeaderActionsHtml,
    getFormModalTitle,
} from "./formShell.js";
import { formItemData, setFormItemData } from "./formModalState.js";
import {
    activateFormModalAccessibility,
    configureDefaultFormActions,
    formModalElement,
} from "./modalShell.js";
import {
    renderDynamicFieldForm,
    bindDatePlaceholderColor,
} from "./dynamicFields.js";
import {
    bindNativeFieldBehavior,
    validateNativeFormFields,
} from "./nativeFieldStyles.js";
import {
    bindLinkedEntityPickerState,
    getTaskAssignmentScopeFromParent,
    updateTaskUserSelectors,
} from "./linkedEntityPicker.js";

bindLinkedEntityPickerState(() => formItemData);

function getDefaultConfigId(configList = []) {
    return configList.find((entry) => entry.default)?.id ||
        configList[0]?.id ||
        "";
}

function getScalarControlValue(field, defaultValue = "") {
    return formItemData?.[field] ?? defaultValue;
}

function getPaymentMethodOptions(selectedValue = "") {
    const methods = Array.isArray(appData?.config?.paymentMethods)
        ? appData.config.paymentMethods
        : [];
    const options = methods.map((method) => {
        if (method && typeof method === "object") {
            const value = String(method.id ?? method.value ?? method.name ?? "");
            return {
                value,
                label: String(method.name ?? method.label ?? value),
                order: Number(method.order ?? 9999),
            };
        }
        return {
            value: String(method ?? ""),
            label: String(method ?? ""),
            order: 9999,
        };
    }).filter((option) => option.value);

    if (selectedValue && !options.some((option) => option.value === selectedValue)) {
        options.push({
            value: selectedValue,
            label: selectedValue,
            selectedOnly: true,
            unavailableLabel: "saved",
            order: 10000,
        });
    }

    return options.sort((optionA, optionB) => {
        const orderDiff = optionA.order - optionB.order;
        return orderDiff || optionA.label.localeCompare(optionB.label);
    });
}

function getPatientCaseManagerValue(isEditing = false) {
    if (isEditing) {return getScalarControlValue("case_manager");}

    const configuredManagerId = appData?.config?.defaultPatientCaseManager || "";
    const allowedUsers = getAssignableUsersWithPermission("patients");
    const configuredManager = allowedUsers.find((user) => {
        return user.id === configuredManagerId && user.isActive !== false;
    });
    return configuredManager?.id || formItemData.case_manager || state.currentUser?.uid || "";
}

function setupParentPeopleControls(type, formFields, isEditing = false) {
    if (type === "patient") {
        const caseManagerRoot = formFields.querySelector(
            '[data-parent-people-control="case_manager"]',
        );
        if (caseManagerRoot) {
            createUserSelectPreset({
                root: caseManagerRoot,
                appData,
                name: "case_manager",
                collectionName: "patients",
                value: getPatientCaseManagerValue(isEditing),
                clearable: false,
                searchable: true,
                onChange: (value) => {
                    formItemData.case_manager = value || "";
                },
            });
        }

        const providerRoot = document.getElementById("form-provider-choices");
        if (providerRoot) {
            createProviderSelectPreset({
                root: providerRoot,
                appData,
                value: Array.isArray(formItemData.provider) ? formItemData.provider : [],
                searchable: true,
                onChange: (value) => {
                    formItemData.provider = Array.isArray(value) ? value : [];
                },
            });
        }
        return;
    }

    if (type === "project") {
        const managerRoot = document.getElementById("form-project_manager-choices");
        if (managerRoot) {
            createUserSelectPreset({
                root: managerRoot,
                appData,
                name: "project_manager",
                collectionName: "projects",
                value: Array.isArray(formItemData.project_manager) ?
                    formItemData.project_manager :
                    [],
                multiple: true,
                searchable: true,
                onChange: (value) => {
                    formItemData.project_manager = Array.isArray(value) ? value : [];
                },
            });
        }
    }
}

function setupParentScalarControls(type, formFields) {
    const controls = {};
    if (type !== "patient" && type !== "project" &&
        type !== "asset" && type !== "task") {return controls;}

    const priorityRoot = formFields.querySelector('[data-parent-scalar-control="priority"]');
    const statusRoot = formFields.querySelector('[data-parent-scalar-control="status"]');
    const priorityDefault = getDefaultConfigId(appData?.config?.priorities || []);
    const statusConfigKey = type === "asset" ? "equipmentStatus" :
        type === "task" ? "taskStatus" :
        type === "patient" ? "patientStatus" : "projectStatus";
    const statusDefault = getDefaultConfigId(appData?.config?.[statusConfigKey] || []);

    if (priorityRoot) {
        controls.priority = createPrioritySelectPreset({
            root: priorityRoot,
            appData,
            value: getScalarControlValue("priority", priorityDefault),
            clearable: false,
        });
    }

    if (statusRoot) {
        controls.status = createStatusSelectPreset({
            root: statusRoot,
            appData,
            itemType: type,
            value: getScalarControlValue("status", statusDefault),
            clearable: false,
        });
    }

    if (type === "asset") {
        const equipmentStatusRoot = formFields.querySelector(
            '[data-parent-scalar-control="equipment_status"]',
        );
        if (equipmentStatusRoot) {
            controls.equipment_status = createStatusSelectPreset({
                root: equipmentStatusRoot,
                appData,
                itemType: "asset",
                value: getScalarControlValue("equipment_status", statusDefault),
                clearable: false,
            });
        }

        ["purchase_date", "installed_on"].forEach((fieldName) => {
            const root = formFields.querySelector(
                `[data-parent-scalar-control="${fieldName}"]`,
            );
            if (!root) {return;}
            controls[fieldName] = createDateControl({
                root,
                name: fieldName,
                type: "date",
                value: getScalarControlValue(fieldName),
            });
        });
        return controls;
    }

    if (type === "task") {
        const dueDateRoot = formFields.querySelector(
            '[data-parent-scalar-control="due_date"]',
        );
        if (dueDateRoot) {
            controls.due_date = createDateControl({
                root: dueDateRoot,
                name: "due_date",
                type: "date",
                value: getScalarControlValue("due_date"),
            });
        }
        return controls;
    }

    if (type === "patient") {
        const paymentRoot = formFields.querySelector(
            '[data-parent-scalar-control="payment_method"]',
        );
        const paymentValue = getScalarControlValue("payment_method");
        if (paymentRoot) {
            controls.payment_method = createSelectControl({
                root: paymentRoot,
                name: "payment_method",
                value: paymentValue,
                options: getPaymentMethodOptions(paymentValue),
                placeholder: "+ Add Payment Method",
                clearable: true,
            });
        }

        const firstSeenRoot = formFields.querySelector(
            '[data-parent-scalar-control="first_seen"]',
        );
        if (firstSeenRoot) {
            controls.first_seen = createDateControl({
                root: firstSeenRoot,
                name: "first_seen",
                type: "date",
                value: getScalarControlValue("first_seen"),
            });
        }

        const appointmentRoot = formFields.querySelector(
            '[data-parent-scalar-control="next_appointment"]',
        );
        if (appointmentRoot) {
            controls.next_appointment = createDateControl({
                root: appointmentRoot,
                name: "next_appointment",
                type: "datetime-local",
                value: getScalarControlValue("next_appointment"),
            });
        }
        return controls;
    }

    const deliveryDateRoot = formFields.querySelector(
        '[data-parent-scalar-control="delivery_date"]',
    );
    if (deliveryDateRoot) {
        controls.delivery_date = createDateControl({
            root: deliveryDateRoot,
            name: "delivery_date",
            type: "date",
            value: getScalarControlValue("delivery_date"),
        });
    }
    return controls;
}

function updateSelectStyles(select, itemType) {
    if (!select) {return;}

    if (!select.value) {
        select.classList.add('text-gray-500', 'dark:text-gray-400');
        select.classList.remove('text-gray-700', 'text-gray-900', 'dark:text-gray-200');
    } else {
        select.classList.remove('text-gray-500', 'dark:text-gray-400');
        select.classList.add('text-gray-700', 'dark:text-gray-200');
    }

    const name = select.name;
    const value = select.value;
    let color = null;

    if (name === 'priority') {
        color = appData?.config?.priorities?.find((priority) =>
            priority.id === value)?.color;
    } else if (name === 'status') {
        const config = appData?.config || {};
        let list = [];
        if (itemType === 'patient') {list = config.patientStatus || [];}
        else if (itemType === 'project') {list = config.projectStatus || [];}
        else if (itemType === 'task') {list = config.taskStatus || [];}
        color = list.find((status) => status.id === value)?.color;
    } else if (name === 'equipment_status') {
        color = appData?.config?.equipmentStatus?.find((status) =>
            status.id === value)?.color;
    }

    if (color) {
        select.style.backgroundColor = color;
        select.style.color = isColorDark(color) ? 'white' : '#1f2937';
        select.style.borderColor = 'transparent';
    } else {
        select.style.backgroundColor = '';
        select.style.color = '';
        select.style.borderColor = '';
    }
}

function getParentTypeControlConfig(type) {
    switch (type) {
        case "patient":
            return {
                configKey: "patientTypes",
                required: true,
                rootId: "form-patient-types-choices",
            };
        case "project":
            return {
                configKey: "projectTypes",
                required: false,
                rootId: "form-project-types-choices",
            };
        case "asset":
            return {
                configKey: "assetTypes",
                required: false,
                rootId: "form-asset-types-choices",
            };
        case "task":
            return {
                configKey: "taskTypes",
                required: false,
                rootId: "form-task-types-choices",
            };
        default:
            return null;
    }
}

function syncTypeDynamicFields(typeOptions, selectedTypeIds, formFields) {
    const itemForm = formFields.closest("form");
    if (!itemForm) {return;}
    const selectedSet = new Set(selectedTypeIds);

    formFields.querySelectorAll('[id^="dynamic-fields-"]:not(#dynamic-fields-anchor)')
        .forEach((container) => {
            const typeId = container.id.replace("dynamic-fields-", "");
            if (!selectedSet.has(typeId)) {
                destroyRegisteredControlsIn(container);
                container.remove();
            }
        });

    selectedTypeIds.forEach((typeId) => {
        const typeConfig = typeOptions.find((option) => option.id === typeId);
        if (typeConfig) {
            renderDynamicFieldForm(typeConfig, itemForm);
        }
    });
    bindNativeFieldBehavior(itemForm);
}

function setupParentMultiSelectFields(type, formFields) {
    const typeControl = getParentTypeControlConfig(type);
    if (typeControl) {
        const root = document.getElementById(typeControl.rootId);
        const selectedTypes = Array.isArray(formItemData.types) ? formItemData.types : [];
        const typeOptions = getScopedTypeOptions(
            appData?.config?.[typeControl.configKey] || [],
            selectedTypes,
        );

        if (root && typeOptions.length === 0) {
            const parent = root.closest("div");
            if (parent) {parent.classList.add("hidden");}
        } else if (root) {
            formItemData.types = selectedTypes;
            createTypeSelectPreset({
                root,
                appData,
                itemType: type,
                options: typeOptions,
                value: selectedTypes,
                required: typeControl.required,
                searchable: true,
                onChange: (value) => {
                    const nextTypes = Array.isArray(value) ? value : [];
                    formItemData.types = nextTypes;
                    syncTypeDynamicFields(typeOptions, nextTypes, formFields);
                },
            });

            syncTypeDynamicFields(typeOptions, selectedTypes, formFields);
        }
    }

    if (type === 'task') {
        updateTaskUserSelectors(
            getTaskAssignmentScopeFromParent(
                formItemData.parentId,
                formItemData.parentType,
            ),
        );
    }

    document.querySelectorAll('input[type="date"], input[type="datetime-local"]')
        .forEach((input) => bindDatePlaceholderColor(input));
}

/**
 * Opens a parent-board add/edit modal.
 * @param {string} type Parent form type.
 * @param {object | null} [item=null] Item data or initial add defaults.
 * @returns {void}
 */
export function openParentForm(type, item = null) {
    if (!isParentFormType(type)) {
        throw new Error(`Unsupported parent form type: ${type}`);
    }
    if (!formModalElement) {
        console.error('Form modal element (#form-modal) not found.');
        return;
    }

    const formTitle = formModalElement.querySelector('#form-title');
    const formHeaderActions = formModalElement.querySelector('#form-header-actions');
    const formFields = formModalElement.querySelector('#form-fields');
    const itemForm = formModalElement.querySelector('#item-form');
    if (!formTitle || !formHeaderActions || !formFields || !itemForm) {
        console.error('Required elements missing within #form-modal.');
        return;
    }

    const isEditing = Boolean(item?.id);
    const formAdapter = getParentFormAdapter(type);
    applyBrandingVariables(formModalElement, appData);
    clearSpecialisedFormShellArtifacts(formModalElement);
    formFields.innerHTML = '';

    setFormItemData(applyAddFormContext(
        cloneFormItemData(item),
        item,
        isEditing,
    ));

    const formContext = {
        appData,
        createDateControl,
        createProviderSelectPreset,
        createPrioritySelectPreset,
        createSelectControl,
        createStatusSelectPreset,
        createStreamScopedStatusSelectPreset,
        createStreamScopedTypeSelectPreset,
        createTypeSelectPreset,
        createUserSelectPreset,
        formFields,
        formItemData,
        formModalElement,
        isEditing,
        item,
        itemForm,
        state,
        type,
    };

    formTitle.textContent = formAdapter?.getTitle?.(formContext) ||
        getFormModalTitle({ type, item, isEditing, findItem });
    formHeaderActions.innerHTML = formAdapter?.getHeaderActionsHtml?.(formContext) ||
        getFormHeaderActionsHtml(type);

    try {
        formFields.innerHTML = formAdapter?.renderFields ?
            formAdapter.renderFields(formContext) :
            getParentFormFields(type, formItemData);
    } catch (error) {
        console.error(`Error generating parent form fields for type "${type}":`, error);
        formFields.innerHTML = '<p class="text-red-500">Error loading form fields.</p>';
    }

    itemForm.dataset.type = type;
    itemForm.dataset.id = formItemData.id || '';
    itemForm.dataset.parentId = formItemData.parentId || '';
    bindNativeFieldBehavior(itemForm);
    itemForm.onsubmit = async (event) => {
        event.preventDefault();
        const nativeValid = validateNativeFormFields(itemForm);
        const registeredValidation = validateRegisteredControls(itemForm);
        if (!nativeValid || !registeredValidation.valid) {
            if (nativeValid) {
                focusFirstRegisteredControlError(registeredValidation);
            }
            return;
        }
        const submitButtons = Array.from(
            itemForm.querySelectorAll('button[type="submit"]'),
        );
        const activeSubmitBtn = event.submitter instanceof HTMLButtonElement
            ? event.submitter
            : submitButtons[submitButtons.length - 1];
        const originalSubmitTexts = new Map(
            submitButtons.map((button) => [button, button.textContent || 'Save']),
        );
        const cancelBtn = document.getElementById('cancel-form-btn');
        const setSubmitProcessingState = (isProcessing, processingLabel = 'Saving...') => {
            submitButtons.forEach((button) => {
                button.disabled = isProcessing;
                button.textContent = isProcessing && button === activeSubmitBtn
                    ? processingLabel
                    : (originalSubmitTexts.get(button) || 'Save');
                button.classList.toggle('opacity-70', isProcessing);
                button.classList.toggle('cursor-wait', isProcessing);
                button.classList.toggle('cursor-not-allowed', isProcessing);
            });

            if (cancelBtn) {
                cancelBtn.disabled = isProcessing;
                cancelBtn.classList.toggle('opacity-70', isProcessing);
                cancelBtn.classList.toggle('cursor-not-allowed', isProcessing);
            }
        };

        await handleParentFormSubmit({
            form: itemForm,
            id: itemForm.dataset.id,
            parentId: itemForm.dataset.parentId,
            setSubmitProcessingState,
            type,
        });
    };

    configureDefaultFormActions();
    const activeFormContext = {
        ...formContext,
        formItemData,
    };
    const scalarControls = formAdapter?.mountScalarControls ?
        formAdapter.mountScalarControls(activeFormContext) :
        setupParentScalarControls(type, formFields);
    if (formAdapter?.mountPeopleControls) {
        formAdapter.mountPeopleControls(activeFormContext);
    } else {
        setupParentPeopleControls(type, formFields, isEditing);
    }

    formFields.querySelectorAll('.currency-input').forEach((input) => {
        input.value = formatCurrency(formItemData[input.name]);
        input.oninput = (event) => {
            let value = event.target.value.replace(/[^0-9.]/g, '');
            if (value.split('.').length > 2) {
                value = value.substring(0, value.lastIndexOf('.'));
            }
            event.target.value = value;
            formItemData[event.target.name] = value;
        };
        input.onblur = (event) => {
            event.target.value = formatCurrency(formItemData[event.target.name]);
        };
        input.onfocus = (event) => {
            event.target.value = formItemData[event.target.name] ?? '';
        };
    });

    requestAnimationFrame(() => {
        if (formAdapter?.mountMultiSelectFields) {
            formAdapter.mountMultiSelectFields(activeFormContext);
        } else {
            setupParentMultiSelectFields(type, formFields);
        }
        formAdapter?.afterControlsMounted?.({
            ...activeFormContext,
            scalarControls,
        });
    });

    formFields.querySelectorAll('select').forEach((select) => {
        updateSelectStyles(select, type);
        select.onchange = (event) => updateSelectStyles(event.target, type);
    });

    formFields.querySelectorAll('input[type="date"], input[type="datetime-local"]')
        .forEach((input) => bindDatePlaceholderColor(input));

    if (type === 'task') {
        setupTaskRecurrenceControls(
            formFields,
            formItemData,
            formModalElement,
            scalarControls,
        );
    }

    if (type === 'patient' && !isEditing) {
        const caseNumInput = formFields.querySelector('input[name="case_file_number"]');
        if (caseNumInput) {
            caseNumInput.value = 'Loading...';
            caseNumInput.classList.add('text-gray-400', 'italic');
            getNextCaseFileNumber().then((code) => {
                if (code) {
                    caseNumInput.value = code;
                    caseNumInput.classList.remove('text-gray-400', 'italic');
                } else {
                    caseNumInput.value = 'Error';
                }
            }).catch((error) => {
                console.error('Failed to load case file number', error);
                caseNumInput.value = 'Manual Entry Required';
            });
        }
    }

    formModalElement.classList.add('flex');
    activateFormModalAccessibility(
        formFields.querySelector("input:not([disabled]), textarea:not([disabled])"),
    );
}
